import { Message } from '@ai-sdk/react';
import { generateId } from 'ai';
import React, { useCallback } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { MessageItem } from './MessageItem';
import { WelcomeWrapper } from './WelcomeWrapper';
import { ThinkingMessage } from './loading/ThinkingMessage';
import { ToolLoadingIndicator } from './loading/ToolLoadingIndicator';
import { WelcomeList } from './welcome-screens/WelcomeList';

// Helper functions for message processing
const isUserMessage = (message: Message): boolean => message.role === 'user';

const isAssistantMessageWithContent = (message: Message): boolean =>
  message.role === 'assistant' && !!message.content?.trim();

/**
 * Returns the index of the last user message in the list.
 */
const getLastUserMessageIndex = (messages: Message[]): number => {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (isUserMessage(messages[i])) return i;
  }
  return -1;
};

/**
 * Checks if there are assistant messages with content after the given index.
 */
const hasAssistantContentAfterIndex = (
  messages: Message[],
  startIndex: number,
): boolean => {
  return messages.slice(startIndex + 1).some(isAssistantMessageWithContent);
};

/**
 * Gets the most recent tool invocation from recent assistant messages during streaming.
 * Used for dynamic loading indicator messages to show what tool is currently executing.
 * Searches through the latest assistant messages to find the most recently invoked tool.
 */
const getCurrentExecutingTool = (messages: Message[]): string | null => {
  if (!messages?.length) return null;

  // Get the last 3 assistant messages for performance - this covers recent tool activity
  // while avoiding processing the entire message history during streaming
  const recentMessages = messages
    .filter((msg) => msg.role === 'assistant')
    .slice(-3);

  // Process messages from most recent to oldest to find the latest tool invocation
  for (let i = recentMessages.length - 1; i >= 0; i--) {
    const message = recentMessages[i];
    if (!message.parts) continue;

    // Find the most recent tool invocation within this message
    // Process parts in reverse order to get the latest tool in the message
    for (let j = message.parts.length - 1; j >= 0; j--) {
      const part = message.parts[j];
      if (part.type === 'tool-invocation' && part.toolInvocation?.toolName) {
        return part.toolInvocation.toolName;
      }
    }
  }

  return null;
};

interface MessagesListProps {
  messages: Message[];
  isWidgetChat: boolean;
  isInProgress: boolean;
  status?: 'ready' | 'streaming' | 'submitted' | 'error';
  error?: Error | null;
  textColor?: string;
  memoizedMessages: any[];
  evmAddress?: string;
  options?: {
    agentName?: string;
    agentImage?: string;
    prompt?: string;
    customComponents?: {
      welcomeMessageComponent?: React.ReactNode;
      loadingIndicator?: React.ComponentType<any>;
    };
  };
  widget?: {
    widgetWelcomePrompts?: {
      title?: string;
      description?: string;
      questions?: string[];
      actions?: string[];
    };
  };
  onAppend: (
    message: Message | { id: string; role: 'user'; content: string },
  ) => void;
  onReload: () => void;
  LoadingIndicator?: React.ComponentType<any>;
}

export const MessagesList: React.FC<MessagesListProps> = ({
  messages,
  isWidgetChat,
  status,
  error,
  textColor,
  memoizedMessages,
  evmAddress,
  options,
  widget,
  onAppend,
  onReload,
}) => {
  const widgetWelcomePrompts = widget?.widgetWelcomePrompts;
  const { title, description, questions, actions } = widgetWelcomePrompts || {};

  const renderWidgetWelcome = useCallback(() => {
    if (!isWidgetChat || !messages || messages.length > 0) return null;

    const overlayClasses = cn(
      'bitte-absolute bitte-top-4 bitte-left-4 bitte-z-50',
      'bitte-text-[#b7bbc1] bitte-overflow-y-scroll bitte-max-h-[90%]',
    );

    const titleClasses = cn('bitte-text-[#FAFAFA] bitte-text-2xl bitte-mb-4');

    return (
      <div className={overlayClasses}>
        <p className={titleClasses}>{title || 'Hello'}</p>
        <p className="bitte-mb-8">
          {description ||
            `I'm your ${options?.agentName || 'AI'} agent. What do you want to achieve today?`}
        </p>
        {questions && questions.length > 0 && (
          <div className="bitte-mb-4">
            <WelcomeList
              items={questions}
              label="Questions"
              bgColor="#60A5FA33"
              onItemClick={(question) =>
                onAppend({
                  id: generateId(),
                  role: 'user',
                  content: question,
                })
              }
            />
          </div>
        )}
        {actions && actions.length > 0 && (
          <WelcomeList
            items={actions}
            label="Actions"
            bgColor="#2D1F1C"
            onItemClick={(action) =>
              onAppend({
                id: generateId(),
                role: 'user',
                content: action,
              })
            }
          />
        )}
      </div>
    );
  }, [
    isWidgetChat,
    messages,
    title,
    description,
    questions,
    actions,
    options?.agentName,
    onAppend,
  ]);

  const renderStandardWelcome = useCallback(() => {
    if (isWidgetChat || !messages || messages.length > 0) return null;

    return (
      <WelcomeWrapper
        messages={messages}
        isWidgetChat={isWidgetChat}
        evmAddress={evmAddress}
        append={onAppend}
        options={options}
      />
    );
  }, [isWidgetChat, messages, evmAddress, onAppend, options]);

  const renderError = useCallback(() => {
    if (!error) return null;

    const errorContainerClasses = cn(
      'bitte-flex bitte-flex-col bitte-items-center bitte-justify-center',
      'bitte-space-y-2 bitte-px-6 bitte-pb-6 bitte-text-center bitte-text-sm',
    );

    return (
      <div className={errorContainerClasses}>
        <p>An error occurred.</p>
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={() => onReload()}
        >
          Retry
        </Button>
      </div>
    );
  }, [error, onReload]);

  const renderMessages = useCallback(() => {
    if (!memoizedMessages || memoizedMessages.length === 0) return null;

    const lastUserMessageIndex = messages
      ? getLastUserMessageIndex(messages)
      : -1;

    const hasAssistantContentAfterUser =
      lastUserMessageIndex >= 0 &&
      messages &&
      hasAssistantContentAfterIndex(messages, lastUserMessageIndex);

    // Tool loading for streaming phase - show throughout streaming
    const shouldShowToolLoading = status === 'streaming';
    const currentExecutingTool = shouldShowToolLoading
      ? getCurrentExecutingTool(messages || [])
      : null;

    // Traditional loading: show while submitted (but NOT streaming) and no assistant content yet
    // When streaming, we use ToolLoadingIndicator instead to avoid conflicts
    const shouldShowThinking =
      status === 'submitted' &&
      lastUserMessageIndex >= 0 &&
      !hasAssistantContentAfterUser &&
      !shouldShowToolLoading;

    const filteredMessages = shouldShowThinking
      ? memoizedMessages.filter(
          ({ message }: any) =>
            message.role !== 'assistant' || message.content?.trim(),
        )
      : memoizedMessages;

    const elementsToRender = filteredMessages.flatMap(
      (messageItemProps: any, index: number) => {
        const items: React.ReactNode[] = [
          <MessageItem
            key={`${index}-${messageItemProps.message.id}`}
            {...messageItemProps}
          />,
        ];

        if (
          shouldShowThinking &&
          lastUserMessageIndex >= 0 &&
          messageItemProps.message.id === messages?.[lastUserMessageIndex]?.id
        ) {
          items.push(
            <ThinkingMessage
              key="thinking-message"
              agentImage={options?.agentImage}
              agentName={options?.agentName}
              isWidgetMode={isWidgetChat}
              disableAnimation={!!options?.prompt}
            />,
          );
        }

        if (
          shouldShowToolLoading &&
          lastUserMessageIndex >= 0 &&
          messageItemProps.message.id === messages?.[lastUserMessageIndex]?.id
        ) {
          items.push(
            <ToolLoadingIndicator
              key="tool-loading-after-user"
              toolName={currentExecutingTool || 'streaming'}
              className="bitte-mt-2"
            />,
          );
        }

        return items;
      },
    );

    return <>{elementsToRender}</>;
  }, [
    memoizedMessages,
    status,
    messages,
    options?.agentImage,
    options?.agentName,
    options?.prompt,
    isWidgetChat,
  ]);

  const containerClasses = cn(
    'bitte-flex bitte-w-full bitte-flex-col bitte-gap-6',
  );

  return (
    <>
      {renderWidgetWelcome()}
      {renderStandardWelcome()}
      <div className={containerClasses} style={{ color: textColor }}>
        {renderMessages()}
        {renderError()}
      </div>
    </>
  );
};
